// Pure, Node-testable core shared by loot.js and forage.js: the registry-id →
// playable-item mapping, the loot-table assembly, and the list-removal guard
// both pickup paths need. No Babylon, no bundler-only imports, so
// tools/invtest.mjs can measure the economy without a browser (the item
// catalog is injected — src/game/items.js is a .js that Node reads as
// CommonJS; see docs/OVERHAUL.md).

// registry-id keyword rules, FIRST MATCH WINS: [regexp, item id, weight scale].
// null = knowingly not spawnable (the weight is skipped, not wasted on a junk
// placeholder). The optional third field exists because a rule that collapses
// thirty registry ids onto ONE playable item multiplies that item's share of
// the table by thirty — every broad collapse carries a scale so the salvage
// it stands in for cannot drown out the table it lives in.
//
// Two things were measured before this list was rewritten (B7): the
// `itm.food.` and `clo.legs.` catch-alls swallowed whole categories, so
// `crackers` alone was 22.3 % of every roll and four ids were 52.9 % of all
// loot; and clo.head/face/hands/feet/rig and res.component/raw/refined had no
// rules at all, which is most of what the military, industrial and
// infrastructure tables are made of.
export const ID_RULES = [
  // ---- packs ----
  [/^clo\.back\.(daypack|rolltop|photographer|school)/, 'backpack_hiking'],
  [/^clo\.back\./, 'backpack_courier'],
  // ---- torso: four garments, so split by what the thing actually is ----
  [/^clo\.torso\.flannel/, 'flannel_shirt'],
  [/^clo\.torso\.(sweater|cardigan|wool)/, 'flannel_shirt'],       // a warm layer, not a coat
  [/^clo\.torso\.(rain|poncho|hazmat|windbreaker|waterproof|shell)/, 'rain_jacket'],
  [/^clo\.torso\.(military|police|paramedic|bomber|leather|duffle|parka|plate|fishing|denim-vest|hi-vis|ghillie|stab-vest|riot-vest|soft-armor)/, 'field_jacket'],
  [/^clo\.torso\./, 'tshirt'],                                     // shirts, tees, hawaiians
  // ---- legs: only two leg garments exist, but they are not interchangeable
  // (shorts is a 1×2 grid, work pants 2×3), so sort by how much pocket the
  // real garment has rather than dumping everything into work_pants ----
  [/^clo\.legs\.(shorts|skirt)/, 'shorts'],
  [/^clo\.legs\..*shorts/, 'shorts'],
  [/^clo\.legs\.(sweatpants|track-pants|thermal|scrub|dress-slacks|formal-suit|corduroy|blue-jeans|black-jeans|jeans)/, 'shorts'],
  [/^clo\.legs\.tarp-wrap/, 'rags', 0.5],
  [/^clo\.legs\./, 'work_pants'],                                  // cargo, hunting, utility, waders
  // ---- head / face / hands / feet / rig: there are no slots for these yet,
  // so the garment is not playable — but the CLOTH is: soft kit becomes a
  // field dressing, rigid kit (helmets, boots, respirators, optics) spawns
  // nothing. When those slots arrive, these are the rules to point at the
  // real garments. ----
  [/^clo\.head\.(bandana|balaclava|beanie|knit|wool|flat-cap|trucker|newsboy|pompom|boonie)/, 'rags', 0.3],
  [/^clo\.head\./, null],                                          // helmets, hard hats, respirators
  [/^clo\.face\.(scarf|gaiter|dust-mask|balaclava|plastic-bag)/, 'rags', 0.3],
  [/^clo\.face\./, null],                                          // shields, goggles, glasses
  [/^clo\.hands\.duct-tape/, 'duct_tape'],
  [/^clo\.hands\.(wool|winter|boxing|gardening)/, 'rags', 0.3],
  [/^clo\.hands\./, null],                                         // leather, welding, rubber gloves
  [/^clo\.feet\.socks/, 'rags', 0.3],
  [/^clo\.feet\./, null],
  [/^clo\.rig\./, null],                                           // no chest-rig slot
  // ---- food: spoilage first, then by family. A swollen tin is a real find
  // with a real cost, and it must not be read as "canned something". ----
  [/^itm\.food\.(bulging|swollen|corroded|rusted|rusty|dented|expired|spoiled|rotten)/, 'bulging_can'],
  [/^itm\.food\.(deadly-webcap|death-cap)/, 'mush_death_cap'],     // the registry knows its mushrooms
  [/^itm\.food\.fly-agaric/, 'mush_fly_agaric'],
  [/^itm\.food\.apple/, 'fruit_apple'],
  [/^itm\.food\.(pear|peach|banana|watermelon|cranberr|corn-ear|beet|cucumber|garlic|potato|onion|tomato)/, 'fruit_apple'],
  [/^itm\.food\.canned-(tuna|clam|beef|chicken)/, 'canned_fish'],
  [/^itm\.food\.canned-(stew|ravioli)/, 'canned_stew'],
  [/^itm\.food\.canned-(peas|asparagus|mushroom)/, 'canned_veg'],
  [/^itm\.food\.canned-fruit/, 'canned_fruit'],
  [/^itm\.food\.canned/, 'canned_soup'],
  [/^itm\.food\..*(candy|caramel|chocolate|granola|rice-crispy|fruit-snacks|donut)/, 'candy_bar'],
  [/^itm\.food\..*(cracker|chip|crisp|pretzel|biscuit|popcorn|bread|roll|trail-mix|jerky)/, 'crackers'],
  [/^itm\.food\.cooked-/, 'canned_stew'],                          // fresh roast doesn't survive on a shelf
  [/^itm\.food\.raw-/, null],                                      // raw meat comes off an animal you butchered
  [/^itm\.food\.(butter|cheese|egg)/, null],
  [/^itm\.food\./, 'crackers'],
  // ---- drink ----
  [/^itm\.drink\.empty/, null],
  [/^itm\.drink\.(tea-bag|tea-mix|coffee|brewed-coffee|powdered-milk|black-tea|herbal-tea)/, null],
  [/^itm\.drink\.(cola|kola|soda|fizz|espresso|birch-beer|sparkling)/, 'soda_can'],
  [/^itm\.drink\..*(vodka|moonshine|whiskey|beer|malt-liquor|schnapps|mead|wine|liquor)/, 'vodka_bottle'],
  [/^itm\.drink\.(purified-water|hydration)/, 'water_bottle'],
  [/^itm\.drink\.(murky|river-water|saltwater)/, 'murky_water'],
  [/^itm\.drink\./, 'juice_can'],
  // ---- med ----
  [/^itm\.med\..*(bandage|gauze|dressing)/, 'bandage'],
  [/^itm\.med\.(suture-kit|needle-and-thread)/, 'bandage'],        // closing a wound, by hand
  [/^itm\.med\.(aspirin|acetaminophen|analgesic|ibuprofen|strong-painkillers)/, 'painkillers'],
  [/^itm\.med\.(cipro|antibiotic|amoxicillin)/, 'antibiotics'],
  [/^itm\.med\.(swabs|betadine|disinfectant|burn|aloe|cooling)/, 'bandage'],
  [/^itm\.med\.(rehydration|salts)/, 'painkillers'],
  // morphine, blood bags, splints, IV kits: no gameplay yet — don't fake them
  [/^itm\.med\./, null],
  // ---- tools ----
  [/^itm\.tool\.matches/, 'matches'],
  [/^itm\.tool\.lighter/, 'matches'],                              // same verb: it lights a campfire
  [/^itm\.tool\.canteen/, 'water_bottle'],
  [/^itm\.tool\.(tarp|sewing-kit)/, 'rags', 0.5],                  // cloth is cloth
  [/^itm\.tool\.(repair-kit|rope)/, 'duct_tape', 0.5],             // binds a wound or a pack strap
  [/^itm\.tool\./, null],                                          // saws, wrenches, compasses: no verb yet
  // ---- valuables: trade goods, all of them ----
  [/^itm\.valuable\.cigarette/, 'cigarettes'],
  [/^itm\.valuable\..*watch/, 'pocket_watch'],
  [/^itm\.valuable\.(vodka|whiskey)/, 'vodka_bottle'],
  [/^itm\.valuable\./, 'wallet'],
  // ---- junk: the cloth half of it is the best bandage stock in the game ----
  [/^itm\.junk\.(rag|cloth-scrap|bedsheet|blanket|towel|pillowcase|burlap|wool-sweater|rubber-glove)/, 'rags', 0.5],
  [/^itm\.junk\.(electrical-tape|super-glue)/, 'duct_tape', 0.5],
  [/^itm\.junk\./, null],                                          // bent nails, crushed cans: no crafting yet
  // ---- resources: tape, cordage and cloth have verbs; metal and glass don't
  // until there is a crafting bench ----
  [/^res\.component\.(tape-duct|zip-tie|wire-metal-spool|rope-wire|chain-link|barbed-wire)/, 'duct_tape', 0.35],
  [/^res\.component\.(canvas-patch|fabric-lining)/, 'rags', 0.5],
  [/^res\.component\./, null],
  [/^res\.raw\.(carrot|potato|beet)/, 'fruit_apple'],
  [/^res\.raw\.plant-fiber/, 'rags', 0.5],
  [/^res\.raw\./, null],
  [/^res\.refined\./, null],
  // ---- everything with its own workstream: weapons, magazines, optics and
  // electronics spawn nothing until there is a verb for them ----
  [/^wpn\./, null],
  [/^att\.ammo\..*(9mm|9x18|380acp|45acp|357mag|44mag)/, 'ammo_pistol'],
  [/^att\.ammo\..*(762|792|545|556|77arisaka|9x39|12gauge)/, 'ammo_rifle'],
  [/^att\.ammo\./, null],                                          // flares, crossbow bolts
  [/^att\./, null],                                                // magazines, optics, grips, stocks
  [/^itm\.electronic\./, null],
  [/^itm\.lore\./, null],
];

const mapCache = new Map();
// registry id → [playable item id | null, weight scale]
export function ruleFor(rid) {
  let out = mapCache.get(rid);
  if (out) return out;
  out = [null, 1];
  for (const [re, id, mul] of ID_RULES) {
    if (re.test(rid)) { out = [id, mul == null ? 1 : mul]; break; }
  }
  mapCache.set(rid, out);
  return out;
}

export function mapId(rid) { return ruleFor(rid)[0]; }

// Rare bonus finds the registry tables cannot express yet. These used to be
// written as `tables.X?.entries.push(...)`, which silently did NOTHING when
// table X did not exist — and military, industrial and infrastructure did not
// exist, because not one of their 60/49/35 registry entries mapped to a
// playable item. That is how the expedition rucksack, the purification
// tablets and the duct tape all ended up unreachable (B5).
const BONUS = [
  ['military', 'backpack_expedition', 2.5],
  ['police', 'backpack_expedition', 2],
  // the hiking pack is the mid-tier upgrade, and the registry only offers it
  // in a handful of civilian tables — put it where a hiker would have left one
  ['rural', 'backpack_hiking', 2],
  ['military', 'purification_tablets', 6],
  ['medical', 'purification_tablets', 8],
  ['civic', 'purification_tablets', 3],
  ['medical', 'antibiotics', 6],
  ['medical', 'bandage', 5],
  // duct tape patches wounds — garages, factories, junk drawers
  ['industrial', 'duct_tape', 3],
  ['vehicle', 'duct_tape', 3],
  ['infrastructure', 'duct_tape', 3],
  ['residential', 'duct_tape', 3],
  ['civilian', 'duct_tape', 3],
  // The three tables whose registry entries are almost all weapons, wiring and
  // scrap metal need a reason to be opened at all. These are what a soldier,
  // a factory hand and a pump-house crew actually leave behind — the registry
  // has no ids for a mess tin or a first-aid cabinet.
  ['military', 'ammo_rifle', 6],
  ['military', 'ammo_pistol', 4],
  ['military', 'canned_stew', 5],
  ['military', 'bandage', 4],
  ['military', 'painkillers', 3],
  ['military', 'field_jacket', 4],
  ['military', 'cigarettes', 3],
  ['industrial', 'matches', 4],
  ['industrial', 'canned_stew', 3],
  ['industrial', 'water_bottle', 3],
  ['industrial', 'cigarettes', 3],
  ['industrial', 'bandage', 3],
  ['industrial', 'vodka_bottle', 2],
  ['infrastructure', 'matches', 3],
  ['infrastructure', 'water_bottle', 3],
  ['infrastructure', 'rags', 3],
  ['infrastructure', 'crackers', 2],
  ['infrastructure', 'bandage', 2],
  ['infrastructure', 'cigarettes', 2],
  // a glovebox and a boot: cloth, a lighter, something to drink
  ['vehicle', 'rags', 5],
  ['vehicle', 'matches', 3],
  ['vehicle', 'crackers', 3],
  ['vehicle', 'soda_can', 3],
  ['vehicle', 'cigarettes', 2],
  ['vehicle', 'water_bottle', 2],
  ['vehicle', 'bandage', 2],
  ['vehicle', 'juice_can', 2],
];

// tag → {rolls, entries:[{id,w}]}. `items` is the playable catalog (ITEMS).
export function buildTables(raw, items) {
  const tables = {};
  for (const t of raw?.tables || []) {
    if (!t?.tag) continue;
    const entries = [];
    for (const e of t.entries || []) {
      const [id, mul] = ruleFor(e.id);
      if (id && items[id] && e.w > 0) entries.push({ id, w: e.w * mul });
    }
    // The table is created even when NOTHING mapped, so the bonus pushes below
    // can still land in it. Empty ones are dropped at the end instead.
    tables[t.tag] = { rolls: Array.isArray(t.rolls) ? t.rolls : [1, 2], entries };
  }
  for (const [tag, id, w] of BONUS) {
    if (!items[id]) continue;                       // never invent an item
    const t = (tables[tag] ||= { rolls: [1, 2], entries: [] });
    t.entries.push({ id, w });
  }
  for (const tag of Object.keys(tables)) {
    const t = tables[tag];
    if (!t.entries.length) { delete tables[tag]; continue; }   // rollSpot must never see an empty table
    // merge duplicates so one id cannot appear three times with three weights
    const byId = new Map();
    for (const e of t.entries) byId.set(e.id, (byId.get(e.id) || 0) + e.w);
    t.entries = [...byId].map(([id, w]) => ({ id, w }));
  }
  return tables;
}

// ---- the list-removal guard (A3) ----------------------------------------
// `list.splice(list.indexOf(ref), 1)` deletes the LAST entry in the list when
// ref is stale, because indexOf returns -1 and splice(-1, 1) counts from the
// end. Both pickup paths go through these two helpers instead.
export function findRef(list, ref, keyOf = (o) => o?.key) {
  if (!ref || !Array.isArray(list)) return null;
  if (list.includes(ref)) return ref;
  const k = keyOf(ref);
  if (k === undefined || k === null) return null;
  return list.find((o) => keyOf(o) === k) || null;
}

export function takeRef(list, ref, keyOf = (o) => o?.key) {
  const live = findRef(list, ref, keyOf);
  if (!live) return null;
  const i = list.indexOf(live);
  if (i < 0) return null;
  return list.splice(i, 1)[0];
}
